import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { IMessageBoxParams } from "../../../../../common/MessageBox";
import { getProjects, IProjectRow } from "../../../../../webapi/service/getProjects";
import { WebAPI } from "../../../../../webapi/constants";
import { Button } from "react-bootstrap";
import BackButton from "../../../common/BackButton";
import UploadSettingFileButton from "../file/UploadSettingFileButton";
import ResetFileButton from "../file/ResetFileButton";
import DownloadConfFileButton from "../file/DownloadConfFileButton";
import { AppConfig, ConfigurationFileType, ListConfig, ProjectStatus, SettingType } from "../../../../../common/constants";
import "../../../../main.css";
import "./simsettings.css";
import "../../projectdetail.css";
import { 
  FormControlLabel, 
  RadioGroup, 
  Radio, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  TextField } from "@mui/material";
import {SelectChangeEvent} from "@mui/material/Select";
import { IFileRow } from "../../../../../webapi/service/getFIles";
import AuthService from "../../../../../common/AuthService";
import { uploadConfFile } from "../../../../../webapi/service/uploadConfFile";
import { deleteConfFile } from "../../../../../webapi/service/deleteConfFile";
import { downloadConfFile } from "../../../../../webapi/service/downloadConfFile";
import { createSimulator } from "../../../../../webapi/service/createSimulator";
import { getSimulator } from "../../../../../webapi/service/getSimulator";
import { getFileLists } from "../../../../../webapi/service/getFileLists";
import { getConfFile } from "../../../../../webapi/service/getConfFile";

export interface IProps {
  /**
   * 認証サービス
   */
  authService?: AuthService;
  /**
   * プロジェクトID
   */
  projectInfo: IProjectRow;
  /**
   * ローディング画面の表示非表示を切り替えます
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoading: (loading: boolean) => void;
  /**
   * 戻るボタンが押された場合に呼び出されます
   */
  onBack: () => void;
  /**
   * プロジェクト一覧画面に戻る場合に呼び出します
   */
  onTop: () => void;
  /**
   * メッセージBoxを表示します
   * @param params - メッセージBoxパラメータ
   */
  showMessage: (params: IMessageBoxParams) => void;
  /**
   * 通信エラーが発生した場合に呼び出します
   * @param id - メッセージID
   * @param status - httpステータスコード
   */
  onCommunicationError: (
    id: string | undefined,
    status: number | undefined
  ) => void;
  /**
   * 非操作タイムアウトをリセットする場合に呼び出します
   */
  resetUnoperatedTimeout: () => void;  
}

export default function SimSettingsFormContents(props: IProps): JSX.Element {
  const type_auto = SettingType.AUTO;
  const type_custom = SettingType.CUSTOM;
  const StatusDoing = ProjectStatus.DOING;
  const simconf = ConfigurationFileType.SIMULATOR_CONF;
  const MenuProps = ListConfig.MENU_PROPS;
  const { 
    authService,
    projectInfo,
    onLoading, 
    onBack, 
    onTop, 
    showMessage, 
    onCommunicationError, 
    resetUnoperatedTimeout 
  } = props;
  const projectid = projectInfo.projectid;
  const { t } = useTranslation();
  const labelAutomode = t("simulator_settings_automode");
  const labelCustommode = t("simulator_settings_custommode");
  const [datafile, setDatafile] = useState<IFileRow[]>([]);
  const [siconffilename, setSiconffilename] = useState<string>("");

  useEffect(() => {
    loadFiles();
    loadConffile();
  }, []);

  /**
   * ファイル一覧を取得する
   */
  const loadFiles = () => {
    onLoading(true);
    getFileLists({
      authService: authService,
      projectid: projectid,
      success: (rows) => {
        if (rows.length === 0) {
          showMessage({
            title: t("simulator_settings_nofile"),
            message: t("simulator_settings_nofile_message"),
            onClose:() => onBack(),
          });
        } else {
          setDatafile(rows);
          setTimeout(() => {
            onLoading(false);
          });
        }        
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  //推定用データ
  const [estimationdata, setEstimationdata] = useState<string>("");
  const handleChangeEstdata = (event: SelectChangeEvent) => {
    setEstimationdata(event.target.value as string);
  }
  //評価用データ
  const [evaluationdata, setEvaluationdata] = useState<string>("");  
  const handleChangeEvaldata = (event: SelectChangeEvent) => {
    setEvaluationdata(event.target.value as string);
  }
  //同定条件（Auto/Custom）
  const [simMode, setSimMode] = useState<string>(type_auto);
  const handleChangeSimMode = (event: SelectChangeEvent) => {
    setSimMode(event.target.value as string);
  }

  //シミュレータ名
  const [simulatorName, setSimulatorName] = useState<string>("");
  const checkName = new RegExp(/^[a-zA-Z0-9-_]+$/);
  const handleChangeSimulatorName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSimulatorName(event.target.value as string);
    checkName.test(event.target.value) ? setInputError(false) : setInputError(true);
  }

  //アップロード中はtrue
  const [uploading, setUploading] = useState<boolean>(false);

  /**
   * 設定ファイル情報取得
   */
  const loadConffile = () => {
    onLoading(true);
    getConfFile({
      authService: authService,
      projectid: projectid,
      type: simconf,
      success: (row) => {
        setSiconffilename(row.name);
        onLoading(false);
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    })
  }

  /**
   * 設定ファイルアップロード
   */
  const handleSelectConfFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if(!files) {
      return;
    }
    if (files.length > AppConfig.MAX_SETTING_FILE) {
      onCommunicationError(WebAPI.ID_NUM_FILE_EXCEED, undefined);
      return;
    }
    for (let i = 0; i < files.length; i++) {
      if (files[i].size > AppConfig.MAX_UPLOAD_FILE_SIZE) { 
        onCommunicationError(WebAPI.ID_FILE_TOO_LARGE, undefined);
        return;
      }
    }
    onLoading(true);
    resetUnoperatedTimeout();
    setUploading(true);
    const fileList: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      fileList.push(file);
    }
    uploadConfFile({
      authService: authService,
      projectid: projectid,
      files: fileList,
      type: simconf,
      success: () => {
        setUploading(false);
        loadConffile();
        onLoading(false);
      },
      fail: (id, status) => {
        setUploading(false);
        onCommunicationError(id, status);
      },
    });  
  }

  /**
   * 設定ファイルリセット
   */
  const handleResetConfFile = () => {
    onLoading(true);
    resetUnoperatedTimeout();
    setUploading(true);
    deleteConfFile({
      authService: authService,
      projectid: projectid,
      type: simconf,
      success: () => {
        setUploading(false);
        loadConffile();
        onLoading(false);
      },
      fail: (id, status) => {
        setUploading(false);
        onCommunicationError(id, status);          
      },
    });
  }

  /**
   * 設定ファイルダウンロード
   */
  const handleDownloadConfFile = () => {
    resetUnoperatedTimeout();
    downloadConfFile({
      authService: authService,
      projectid: projectid,
      type: simconf,
      filename: "conffile_" + projectid,
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  /**
   * シミュレータ作成
   */
  const handleCreateSimulator = () => {
    const maxSimulators = AppConfig.MAX_SIMULATORS;
    const existSimNameList: string[] = [];
    resetUnoperatedTimeout();
    getSimulator({
      authService: authService,
      projectid: projectid,
      success: (rows) => {
        for (let i = 0; i < rows.length; i++) {
          const existSimName = rows[i].name;
          existSimNameList.push(existSimName);
        }
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
    getProjects({
      authService: authService,
      projectid: projectid,
      success: (rows) => {
        const row = rows[0];
        setTimeout(() => {
          if (row.simcreatests === StatusDoing) {
            showMessage({
              title: t("simcreatests_doing"),
              message: t("simcreatests_doing_message"),
            });
          } else if (row.simevalsts === StatusDoing) {
            showMessage({
              title: t("simevalsts_doing"),
              message: t("simevalsts_doing_message"),
            });
          } else if (row.fkdpplearnsts === StatusDoing) {
            showMessage({
              title: t("fkdpplearnsts_doing"),
              message: t("fkdpplearnsts_doing_message"),
            });
          } else if (row.fkdppevalsts === StatusDoing) {
            showMessage({
              title: t("fkdppevalsts_doing"),
              message: t("fkdppevalsts_doing_message"),
            });
          } else if (row.fkdppsimsts === StatusDoing) {
            showMessage({
              title: t("fkdppsimsts_doing"),
              message: t("fkdppsimsts_doing_message"),
            });
          } else {            
            if (existSimNameList.length >= maxSimulators) {
              showMessage({
                title: t("sumulator_number_max"),
                message: t("simulator_number_max_caption"),
              });
            } else if (existSimNameList.indexOf(simulatorName) === -1) {
              onLoading(true);
              createSimulator({
                authService: authService,
                projectid: projectid,
                trainfile: estimationdata,
                validationfile: evaluationdata,
                name: simulatorName,
                type: simMode,
                success: () => {
                  onLoading(false);
                  onTop();
                },
                fail: (id, status) => {
                  onLoading(false);
                  onCommunicationError(id, status);
                },
              });
            } else {
              showMessage({
                title: t("sumulator_name_duplicate"),
                message: t("simulator_name_duplicate_caption"),
              });
            }
          }
        });
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  };

  /**
   * ファイル一覧情報から選択肢を生成
   */
  const fileList = datafile.map((listvalue) => {
    return (
      <MenuItem 
        key={listvalue.id} 
        value={listvalue.fileid}
      >
        {listvalue.name}
      </MenuItem>
    );
  })

  /**
   * 各種入力フォームの作成
   */
  function createTrainfileForm():JSX.Element {
    let contents = <div />
    contents = (
      <div className="contents">
        <div className="contents">{t("simulator_settings_trainfile")}</div>
          <FormControl required sx={{ mr: 1, width: 1 / 2 }}>
            <InputLabel id="trainfile-label">{t("simulator_settings_trainfile")}</InputLabel>
            <Select
              labelId="trainfile-label"
              id="trainfile"
              required={true}
              value={estimationdata}
              label={t("simulator_settings_trainfile")}
              MenuProps={MenuProps}
              onChange={handleChangeEstdata}
            >
              {fileList}
            </Select>
          </FormControl>
      </div>
    );
    return contents;
  }
  const trainfileForm = createTrainfileForm();

  function createEvaluationfileForm():JSX.Element {
    let contents = <div />
    contents = (
      <div className="contents">
        <div className="contents">{t("simulator_settings_evaluationfile")}</div>
        <FormControl required sx={{ mr: 1, width: 1 / 2 }}>
          <InputLabel id="evaluationfile-label">{t("simulator_settings_evaluationfile")}</InputLabel>
          <Select
            labelId="evaluationfile-label"
            id="evaluationfile"
            value={evaluationdata}
            label={t("simulator_settings_evaluationfile")}
            MenuProps={MenuProps}
            onChange={handleChangeEvaldata}
          >
            {fileList}
          </Select>
        </FormControl>
      </div>
    );
    return contents;
  }
  const evaluationfileForm = createEvaluationfileForm();

  const [inputError, setInputError] = useState(false);
  function createSimnameForm():JSX.Element {
    let contents = <div />
    contents = (
      <div className="contents">
        <div className="contents">{t("simulator_settings_name")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 2 }}>
          <TextField 
            id="name" 
            required={true}
            value={simulatorName}
            label={t("simulator_settings_name")} 
            variant="outlined"
            onChange={handleChangeSimulatorName}
            error={inputError}
            helperText={t("simulator_settings_name_error")}
            inputProps={{ maxLength: 50 }}
          ></TextField>
        </FormControl>
      </div>
    );
    return contents;
  }
  const simnameForm = createSimnameForm();

  function createModeRadiobtn():JSX.Element {
    let contents = <div />
    contents = (
      <div className="contents">
        <div className="contents">{t("simulator_settings_mode")}</div>
          <FormControl sx ={{ mr: 1 }}>
            <RadioGroup 
              row
              aria-label="mode" name="mode1"
              value={simMode}
              onChange={handleChangeSimMode}
            >
              <Box sx ={{width: 300, p:1}}>
                <FormControlLabel value={type_auto} control={<Radio />} label={labelAutomode} />
              </Box>
              <Box sx ={{width: 300, p:1}}>
                <FormControlLabel value={type_custom} control={<Radio />} label={labelCustommode} />
              </Box>
            </RadioGroup>                 
          </FormControl>
      </div>
    );
    return contents;
  }
  const modeRadiobtn = createModeRadiobtn();

  function createCustommodeMenu():JSX.Element {
    let contents = <div />;
    let filename = <div />;
    filename = <Box sx ={{width: 1 / 2, p:1}}>{t("name_configfile")}{siconffilename}</Box>;
    contents = (
      <div className="contents">
        <div className="contents">{t("simulator_settings_customfiles")}</div>       
        <div className="contents">
          <UploadSettingFileButton onChange={handleSelectConfFile}/>               
          <DownloadConfFileButton onClick={handleDownloadConfFile}/>
          <ResetFileButton onClick={handleResetConfFile}/> 
        </div>
        {filename}
      </div>
    );
    return contents;
  }
  const custommodeMenu = (simMode === type_custom && uploading === false) ?
    createCustommodeMenu() : "";

  function createSimulatebtn(): JSX.Element {
    let contents = <div />;
    let btntype = <div />;
    if (estimationdata && evaluationdata && simulatorName && !inputError) {
      btntype = (
        <Button className="btn-mainspace" onClick={handleCreateSimulator}>
          {t("simulator_settings_create")}
        </Button>
      );
    } else {
      btntype = (
        <Button className="btn-mainspace" disabled>
          {t("simulator_settings_create")}
        </Button>
      );
    }
    contents = <div className="contents">{btntype}</div>;
    return contents;
  }
  const simulateBtn = createSimulatebtn();

  return (
    <div className="mainspace">
      <div className="mainspace-title">
        <h3>{t("project_detail_project_name")}</h3>
        <h3 style={{ marginLeft: "10px" }}>{projectInfo.name}</h3>
      </div>
      <div className="mainspace-toolbar">
        <BackButton onClick={onBack} />
      </div>
      <div className="mainspace-toolbar">
        <h3>{t("simulator_settings")}</h3>
      </div>
      <div className="form">
        {trainfileForm}
        {evaluationfileForm}
        {simnameForm}
        {modeRadiobtn}
        {custommodeMenu}
        {simulateBtn}            
      </div>          
    </div>
  );
}

